import { Controller } from "@hotwired/stimulus";

// Tells the server when the customer has actually LOOKED at their Kit panel.
//
// KitController#show already records the moment the drawer loaded, but the
// panel then stays open for hours while Kit — and, during a takeover, a human —
// answers into it over the websocket. Without this, every reply sent to an
// already-open panel would read as "not seen" forever in the operator console,
// which is the one case an operator most needs the truth about.
//
// The bar for posting is deliberately "rendered in front of a person", not
// "delivered":
//
//   * document.hidden means the tab is backgrounded — the message arrived, but
//     nobody read it, and saying otherwise is the lie this whole feature exists
//     to avoid.
//   * A hidden tab that later comes back to the front posts then, on
//     visibilitychange, because that IS the moment they saw it.
//
// Throttled to one post per THROTTLE_MS: a Kit answer streams in as a burst of
// mutations and each one would otherwise be its own request.
export default class extends Controller {
  static values = { url: String, chatId: Number };

  connect() {
    this.THROTTLE_MS = 5000;
    this.lastSent = 0;
    this.pending = false;

    // Only new nodes count. Kit streams an answer in by replacing the same
    // bubble many times, and reacting to attribute or text changes would post
    // on every token.
    this.observer = new MutationObserver((records) => {
      for (const record of records) {
        if (record.addedNodes && record.addedNodes.length) {
          this.report();
          return;
        }
      }
    });
    this.observer.observe(this.element, { childList: true, subtree: true });

    this.onVisibility = () => {
      if (!document.hidden && this.pending) this.report();
    };
    document.addEventListener("visibilitychange", this.onVisibility);
  }

  disconnect() {
    if (this.observer) this.observer.disconnect();
    document.removeEventListener("visibilitychange", this.onVisibility);
  }

  report() {
    // Backgrounded: remember that something arrived unseen, and post when they
    // come back to the tab.
    if (document.hidden) {
      this.pending = true;
      return;
    }

    const now = Date.now();
    if (now - this.lastSent < this.THROTTLE_MS) return;
    this.lastSent = now;
    this.pending = false;

    if (!this.hasUrlValue || !this.hasChatIdValue) return;

    const token = document.querySelector('meta[name="csrf-token"]');
    fetch(this.urlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": token ? token.getAttribute("content") : "",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ chat_id: this.chatIdValue }),
      // The panel must never be taken down by a receipt.
      keepalive: true,
    }).catch(() => {
      // Offline, or the tab is closing. The next render will re-report.
    });
  }
}
